import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { SuggestionCitationSummary, SuggestionResponse } from 'we-tree-api-client/v2';
import ImageEnlargeOverlay from '@/components/shared/ImageEnlargeOverlay';
import { getDiffPathLabel } from '@/types/suggestion';
import { SOURCE_TYPE_LABELS } from '@/components/sources/sourceTypeMeta';

import './suggestion-evidence-panel.css';

export interface SuggestionEvidencePanelProps {
  suggestion: SuggestionResponse;
}

function sourceTypeLabel(type?: string): string | undefined {
  if (!type) return undefined;
  return (SOURCE_TYPE_LABELS as Record<string, string>)[type] ?? type;
}

const SuggestionEvidencePanel: React.FC<SuggestionEvidencePanelProps> = ({ suggestion }) => {
  const { t } = useTranslation('forms');
  const { t: tCommon } = useTranslation('common');
  const [imageOverlayMediaId, setImageOverlayMediaId] = useState<string | null>(null);
  const citations = suggestion.citations ?? [];
  const authorName = suggestion.createdByNickname?.trim() || t('suggest.aFamilyMember');

  if (citations.length === 0) {
    return (
      <div className="suggestion-evidence-panel" data-tour="suggestion-evidence">
        <h3 className="suggestion-evidence-panel__title">{t('suggest.evidenceHeading')}</h3>
        <p className="suggestion-evidence-panel__testimony">{t('suggest.familyTestimony', { author: authorName })}</p>
      </div>
    );
  }

  return (
    <div className="suggestion-evidence-panel" data-tour="suggestion-evidence">
      <h3 className="suggestion-evidence-panel__title">{t('suggest.evidenceHeading')}</h3>
      <ul className="suggestion-evidence-panel__list" aria-label={tCommon('aria.attachedCitations')}>
        {citations.map((citation: SuggestionCitationSummary) => {
          const typeLabel = sourceTypeLabel(citation.sourceType);
          return (
            <li key={citation.id} className="suggestion-evidence-panel__item">
              <div className="suggestion-evidence-panel__item-main">
                <p className="suggestion-evidence-panel__source">
                  {citation.sourceTitle || t('suggest.untitledSource')}
                  {typeLabel ? <span className="suggestion-evidence-panel__type"> · {typeLabel}</span> : null}
                </p>
                {citation.sourceUrl ? (
                  <a
                    className="suggestion-evidence-panel__url"
                    href={citation.sourceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {citation.sourceUrl}
                  </a>
                ) : null}
                {citation.detailLocator ? (
                  <p className="suggestion-evidence-panel__meta">
                    {t('suggest.locatorMeta', { value: citation.detailLocator })}
                  </p>
                ) : null}
                {citation.notes ? <p className="suggestion-evidence-panel__meta">{citation.notes}</p> : null}
                {citation.targetPath ? (
                  <p className="suggestion-evidence-panel__meta">
                    {t('suggest.supportsMeta', { label: getDiffPathLabel(citation.targetPath) })}
                  </p>
                ) : null}
              </div>
              {citation.mediaAssetId ? (
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  onClick={() => setImageOverlayMediaId(citation.mediaAssetId!)}
                >
                  {t('citations.viewImage')}
                </button>
              ) : null}
            </li>
          );
        })}
      </ul>

      {imageOverlayMediaId && (
        <ImageEnlargeOverlay
          isOpen
          familyId={suggestion.familyId}
          mediaAssetId={imageOverlayMediaId}
          onClose={() => setImageOverlayMediaId(null)}
        />
      )}
    </div>
  );
};

export default SuggestionEvidencePanel;
